'use client'
import { useEffect, useState, Suspense } from 'react'
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import SessionTimeoutAlert from '@/components/SessionTimeoutAlert';
import Footer from '@/components/Footer'
import FloatingLoginButton from '@/components/FloatingLoginButton'
import PopupBanner from '@/components/PopupBanner'
import VisitorCounter from '@/components/VisitorCounter'
import seoConfig from '@/config/seo';
import { MembershipProvider } from '@/contexts/MembershipContext';
import { PromoProvider } from '@/contexts/PromoContext';
import { fetchHotelTheme } from '@/services/api'
import DynamicFavicon from '@/components/DynamicFavicon';

export default function ClientLayout({ children, hotelDetails, facilities }) {
  const [isLoading, setIsLoading] = useState(true);
  const [showMembershipButton, setShowMembershipButton] = useState(true);
  const [useMembership, setUseMembership] = useState(null);
  const resetSession = useSessionTimeout();
  const pathname = usePathname();


  // Force the document title and fetch theme details
  useEffect(() => {
    // Set the title immediately
    document.title = 'Booking Engine';
    
    // Also set it again after a short delay to override any late title changes
    const titleInterval = setInterval(() => {
      if (document.title !== 'Booking Engine') {
        document.title = 'Booking Engine';
      }
    }, 500);
    
    // Setup default theme values without API
    setupDefaultTheme();
    
    // Fetch hotel details to check use_membership flag
    const getHotelDetails = async () => {
      try {
        const hotelData = await fetchHotelTheme();
        // Check if use_membership flag is available in the response
        if (hotelData && hotelData.use_membership !== undefined) {
          // Convert to number and ensure it's a valid number
          const rawValue = hotelData.use_membership;
          const membershipValue = Number(rawValue);
          
          console.log('🔍 ClientLayout - Raw use_membership from API:', rawValue, 'Type:', typeof rawValue);
          console.log('🔍 ClientLayout - Converted to number:', membershipValue, 'Type:', typeof membershipValue);
          
          setUseMembership(membershipValue);
          
          // Set visibility of membership button based on use_membership flag
          // Mode 0: No button
          // Mode 1: FloatingLoginButton (show)
          // Mode 2: Reserved (show FloatingLoginButton for now)
          // Mode 3: Guest Portal button (hide FloatingLoginButton, show custom button in page.js)
          const shouldShowFloatingLoginButton = membershipValue !== 0 && membershipValue !== 3;
          setShowMembershipButton(shouldShowFloatingLoginButton);
          
          console.log('🔍 ClientLayout - Membership Configuration:');
          console.log('   - use_membership value:', membershipValue);
          console.log('   - Comparison check (membershipValue !== 3):', membershipValue !== 3);
          console.log('   - Comparison check (membershipValue === 3):', membershipValue === 3);
          console.log('   - FloatingLoginButton visibility:', shouldShowFloatingLoginButton);
          console.log('   - Mode explanation:');
          if (membershipValue === 0) {
            console.log('     → Mode 0: No membership button');
          } else if (membershipValue === 1) {
            console.log('     → Mode 1: FloatingLoginButton (Member Login)');
          } else if (membershipValue === 2) {
            console.log('     → Mode 2: Reserved (showing FloatingLoginButton)');
          } else if (membershipValue === 3) {
            console.log('     → Mode 3: Guest Portal button (in page.js)');
          } else {
            console.log('     → Unknown mode:', membershipValue);
          }
        } else {
          console.warn('⚠️ ClientLayout - use_membership not found in API response');
          console.log('   - hotelData:', hotelData);
        }
      } catch (error) {
        // Silently handle error without showing error overlay
        console.error('Error fetching hotel details for membership flag:', error);
        // Default to showing the button in case of error
        setShowMembershipButton(true);
      } finally {
        // Mark loading as complete
        setIsLoading(false);
      }
    };
    
    getHotelDetails();
    
    // Clean up interval on unmount
    return () => {
      clearInterval(titleInterval);
    };
  }, []);

  // Setup default theme values without fetching from API
  const setupDefaultTheme = () => {
    // Default colors
    const primaryColor = '#7b9903';
    const secondaryColor = '#FFFFFF';
    const tertiaryColor = '#FFFFFF';
    
    // Convert hex to RGB for primary color
    const primaryR = parseInt(primaryColor.slice(1, 3), 16);
    const primaryG = parseInt(primaryColor.slice(3, 5), 16);
    const primaryB = parseInt(primaryColor.slice(5, 7), 16);
    
    // Convert hex to RGB for secondary color
    const secondaryR = parseInt(secondaryColor.slice(1, 3), 16);
    const secondaryG = parseInt(secondaryColor.slice(3, 5), 16);
    const secondaryB = parseInt(secondaryColor.slice(5, 7), 16);
    
    // Convert hex to RGB for tertiary color
    const tertiaryR = parseInt(tertiaryColor.slice(1, 3), 16);
    const tertiaryG = parseInt(tertiaryColor.slice(3, 5), 16);
    const tertiaryB = parseInt(tertiaryColor.slice(5, 7), 16);
    
    // Calculate lighter and darker variants
    const primaryLight = adjustColorBrightness(primaryColor, 20);
    const primaryDark = adjustColorBrightness(primaryColor, -30);
    const secondaryLight = adjustColorBrightness(secondaryColor, 10);
    const secondaryDark = adjustColorBrightness(secondaryColor, -10);
    
    // Determine text colors
    const textOnPrimary = getContrastColor(primaryColor);
    const textOnSecondary = getContrastColor(secondaryColor);
    
    // Set CSS variables
    document.documentElement.style.setProperty('--primary-color', primaryColor);
    document.documentElement.style.setProperty('--primary-color-rgb', `${primaryR}, ${primaryG}, ${primaryB}`);
    document.documentElement.style.setProperty('--primary-light', primaryLight);
    document.documentElement.style.setProperty('--primary-dark', primaryDark);
    document.documentElement.style.setProperty('--primary-contrast', textOnPrimary);
    
    document.documentElement.style.setProperty('--secondary-color', secondaryColor);
    document.documentElement.style.setProperty('--secondary-color-rgb', `${secondaryR}, ${secondaryG}, ${secondaryB}`);
    document.documentElement.style.setProperty('--secondary-light', secondaryLight);
    document.documentElement.style.setProperty('--secondary-dark', secondaryDark);
    
    document.documentElement.style.setProperty('--tertiary-color', tertiaryColor);
    document.documentElement.style.setProperty('--tertiary-color-rgb', `${tertiaryR}, ${tertiaryG}, ${tertiaryB}`);
    
    document.documentElement.style.setProperty('--text-on-primary', textOnPrimary);
    document.documentElement.style.setProperty('--text-on-secondary', textOnSecondary);
    
    document.documentElement.style.setProperty('--button-hover', primaryLight);
    document.documentElement.style.setProperty('--button-active', primaryDark);
    
    document.documentElement.style.setProperty('--surface-color', secondaryColor);
    document.documentElement.style.setProperty('--surface-color-rgb', `${secondaryR}, ${secondaryG}, ${secondaryB}`);
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Helper function to adjust color brightness
  const adjustColorBrightness = (hex, percent) => {
    hex = hex.replace(/^\s*#|\s*$/g, '');
    if(hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];

    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const calculateNewValue = (value) => {
      value = parseInt(value * (100 + percent) / 100);
      return value < 255 ? value : 255;
    }

    const rr = ((calculateNewValue(r) | 1 << 8).toString(16)).slice(1);
    const gg = ((calculateNewValue(g) | 1 << 8).toString(16)).slice(1);
    const bb = ((calculateNewValue(b) | 1 << 8).toString(16)).slice(1);

    return `#${rr}${gg}${bb}`;
  }
  
  // Helper function to determine contrast color
  const getContrastColor = (hexColor) => {
    hexColor = hexColor.replace('#', '');
    const r = parseInt(hexColor.substr(0, 2), 16);
    const g = parseInt(hexColor.substr(2, 2), 16);
    const b = parseInt(hexColor.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  }

  // Determine current page
  let page = 'home';
  if (pathname.startsWith('/booking')) {
    page = 'rooms';
  } else if (pathname.startsWith('/promo')) {
    page = 'promo';
  } else if (pathname.startsWith('/reservation')) {
    page = 'reservations';
  } else if (pathname === '/') {
    page = 'home';
  }

  return (
    <MembershipProvider>
      <PromoProvider>
        <Suspense fallback={null}>
          <DynamicFavicon />
        </Suspense>
        <div className="font-sans bg-gray-50 text-gray-900 min-h-screen flex flex-col" style={{visibility: isLoading ? 'hidden' : 'visible'}}>
          <div>
            <SessionTimeoutAlert onReset={resetSession} />
            <PopupBanner page={page} />
            <VisitorCounter page={pathname} />
            <main>
              <h1 className="sr-only">{seoConfig[page]?.title || seoConfig.default.title}</h1>
              {children}
            </main>
          </div>
          {showMembershipButton && <FloatingLoginButton />}
          <Footer hotelTheme={{NAME: 'Booking Engine'}} />
        </div>
      </PromoProvider>
    </MembershipProvider>
  );
} 